package com.devi3east.bashbuddy.auth.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.auth.util.RecoveryTokenStore
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.InfoAlertDialog
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.app.data.Screen
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun ResetPasswordScreenRoute(
    appState: AppState,
    deepLinkRoute: String = "",
    authViewModel: AuthViewModel = koinViewModel()
) {
    val tokenFromStore = RecoveryTokenStore.token
    val snackbarHostState = remember { SnackbarHostState() }
    var isProcessing by remember { mutableStateOf(false) }
    var showResetDialog by remember { mutableStateOf(false) }

    LaunchedEffect(deepLinkRoute, tokenFromStore) {
        val tokenFromDeepLink = authViewModel.parseParamFromDeepLink(deepLinkRoute, "token")
            .ifEmpty { authViewModel.parseParamFromDeepLink(deepLinkRoute, "access_token") }

        val errorCodeFromDeepLink = authViewModel.parseParamFromDeepLink(deepLinkRoute, "error_code")
            .ifEmpty { authViewModel.parseParamFromDeepLink(deepLinkRoute, "error") }

        val hasError = errorCodeFromDeepLink.contains("otp_expired", ignoreCase = true) ||
                errorCodeFromDeepLink.contains("invalid", ignoreCase = true)

        if (hasError) {
            RecoveryTokenStore.token = null
            showResetDialog = true
            return@LaunchedEffect
        }

        val storeMissing = authViewModel.tokenIsMissing(tokenFromStore)
        val deepLinkMissing = authViewModel.tokenIsMissing(tokenFromDeepLink)

        if (storeMissing && deepLinkMissing) {
            showResetDialog = true
        } else {
            showResetDialog = false
        }
    }

    LaunchedEffect(Unit) {
        authViewModel.uiEvent.collect { event ->
            when (event) {
                is AuthUiEvent.ShowMessage -> {
                    snackbarHostState.showSnackbar(event.message)
                    val appNavController = appState.navController
                    if (event.message.contains("success", ignoreCase = true)) {
                        isProcessing = false
                        appNavController.navigate(Screen.EventsScreen.route) {
                            popUpTo(Screen.EventsScreen.route) { inclusive = true }
                            launchSingleTop = true
                        }
                    } else {
                        isProcessing = false
                    }
                }
                else -> Unit
            }
        }
    }

    ResetPasswordScreen(
        appState = appState,
        snackbarHostState = snackbarHostState,
        isProcessing = isProcessing,
        onSubmit = { newPassword ->
            isProcessing = true
            authViewModel.updatePassword(newPassword, tokenFromStore ?: "")
        },
        onCancel = {
            isProcessing = true
            appState.navController.navigate(Screen.EventsScreen.route) {
                popUpTo(Screen.EventsScreen.route) { inclusive = true }
                launchSingleTop = true
            }
        },
        showResetDialog = showResetDialog,
        onDismissResetDialog = {
            showResetDialog = false
            appState.navController.navigate(Screen.EventsScreen.route) {
                popUpTo(Screen.EventsScreen.route) { inclusive = true }
                launchSingleTop = true
            }
        }
    )
}

@Composable
fun ResetPasswordScreen(
    appState: AppState,
    snackbarHostState: SnackbarHostState,
    isProcessing: Boolean,
    onSubmit: (String) -> Unit,
    onCancel: () -> Unit,
    showResetDialog: Boolean,
    onDismissResetDialog: () -> Unit
) {
    var newPassword by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var passwordVisible by remember { mutableStateOf(false) }
    var confirmPasswordVisible by remember { mutableStateOf(false) }

    val passwordValid = newPassword.length >= 8
    val passwordsMatch = newPassword == confirmPassword
    val canSubmit = passwordValid && passwordsMatch
    val scrollState = rememberScrollState()

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        Column(
            modifier = contentModifier.padding(horizontal = 16.dp)
        ) {
            Text(
                text = "Reset Your Password",
                style = MaterialTheme.typography.titleLarge.copy(
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
            )

            OutlinedTextField(
                value = newPassword,
                onValueChange = { newPassword = it },
                label = { Text("New Password") },
                visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                trailingIcon = {
                    IconButton(onClick = { passwordVisible = !passwordVisible }) {
                        Icon(
                            imageVector = if (passwordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff,
                            contentDescription = if (passwordVisible) "Hide password" else "Show password"
                        )
                    }
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp)
            )

            OutlinedTextField(
                value = confirmPassword,
                onValueChange = { confirmPassword = it },
                label = { Text("Confirm Password") },
                visualTransformation = if (confirmPasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                trailingIcon = {
                    IconButton(onClick = { confirmPasswordVisible = !confirmPasswordVisible }) {
                        Icon(
                            imageVector = if (confirmPasswordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff,
                            contentDescription = if (confirmPasswordVisible) "Hide password" else "Show password"
                        )
                    }
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp)
            )

            if (!passwordsMatch && confirmPassword.isNotEmpty()) {
                Text(
                    text = "Passwords do not match",
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodyMedium
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(
                    onClick = onCancel,
                    enabled = !isProcessing
                ) {
                    Text(
                        "Cancel",
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                Spacer(Modifier.width(8.dp))

                Button(
                    onClick = { onSubmit(newPassword) },
                    enabled = canSubmit && !isProcessing,
                    shape = RoundedCornerShape(12.dp),
                    elevation = ButtonDefaults.buttonElevation(defaultElevation = 2.dp)
                ) {
                    Text("Submit")
                }
            }
        }

        if (isProcessing) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.45f)),
                contentAlignment = Alignment.Center
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    CircularProgressIndicator(
                        color = MaterialTheme.colorScheme.primary,
                        strokeWidth = 4.dp,
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(Modifier.height(12.dp))
                    Text(
                        text = "You’ll be redirected back to the app shortly...",
                        style = MaterialTheme.typography.bodyMedium.copy(color = Color.White),
                        textAlign = TextAlign.Center
                    )
                }
            }
        }

        if (showResetDialog) {
            InfoAlertDialog(
                title = "Recovery Link Error",
                message = "The recovery link has expired or is invalid. Please request a new link to reset your password.",
                onDismiss = onDismissResetDialog
            )
        }
    }
}